import { css } from '@firebolt-dev/css'
import { useEffect, useRef, useState } from 'react'
import { storage } from '../../core/storage'
import { UserRoundIcon } from 'lucide-react'

const MAX_BYTES = 10 * 1024 * 1024

export function AvatarChooser({ world, required = false, onClose, onSelected = onClose }) {
  const player = world.entities.player
  const [catalog, setCatalog] = useState([])
  const [selected, setSelected] = useState(required ? null : world.entities.player?.data.avatar)
  const [name, setName] = useState(() => required ? '' : (player?.data.name || ''))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const fileRef = useRef()
  const apiUrl = world.network.apiUrl

  useEffect(() => {
    fetch(`${apiUrl}/avatars`)
      .then(async response => {
        if (!response.ok) throw new Error('Catalogo non disponibile')
        return response.json()
      })
      .then(setCatalog)
      .catch(err => setError(err.message))
  }, [apiUrl])

  async function request(path, body) {
    setBusy(true)
    setError('')
    try {
      const response = await fetch(`${apiUrl}${path}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${storage.get('authToken')}`,
          ...(body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
        },
        body: body instanceof FormData ? body : JSON.stringify(body),
      })
      const result = await response.json()
      if (!response.ok) throw new Error(result.error || 'Impossibile cambiare avatar')
      setSelected(result.url)
      onSelected()
    } catch (err) {
      setError(err.message)
    } finally {
      setBusy(false)
    }
  }

  function upload(file) {
    if (!file) return
    if (!file.name.toLowerCase().endsWith('.vrm')) {
      setError('Seleziona un file .vrm')
      return
    }
    if (file.size > MAX_BYTES) {
      setError('Il VRM supera 10 MB')
      return
    }
    const form = new FormData()
    form.append('file', file)
    request('/avatar/upload', form)
  }

  function saveName() {
    const nextName = name.trim()
    if (!nextName) {
      if (!required) setName(player?.data.name || '')
      return
    }
    setName(nextName)
    if (!required && nextName !== player?.data.name) player?.setName(nextName)
  }

  function selectAvatar(url) {
    const nextName = name.trim()
    if (required && !nextName) {
      setError('Inserisci il tuo nome prima di scegliere l’avatar')
      return
    }
    request('/avatar/select', required ? { url, name: nextName } : { url })
  }

  return (
    <div
      className={`avatar-chooser-backdrop${required ? ' required' : ''}`}
      css={css`
        position: absolute;
        inset: 0;
        z-index: 20;
        background: rgba(0, 0, 0, 0.45);
        display: grid;
        place-items: center;
        padding: 1rem;
        pointer-events: none;
        &.required { pointer-events: auto; }
        .avatar-chooser {
          pointer-events: auto;
          width: min(100%, 38rem);
          max-height: 90vh;
          overflow-y: auto;
          /* Matches the two stacked dark layers of the default sidebar pane. */
          background: rgba(11, 10, 21, 0.985);
          color: white;
          border: 1px solid rgba(255, 255, 255, 0.05);
          border-radius: 1.375rem;
          backdrop-filter: blur(5px);
          box-shadow: 0 1rem 4rem rgba(0, 0, 0, 0.25);
          font-family: inherit;
          font-size: 1rem;
        }
        h2 {
          margin: 0;
          padding: 1rem 1.25rem;
          border-bottom: 1px solid rgba(255, 255, 255, 0.05);
          font-size: 1rem;
          font-weight: 500;
        }
        .avatar-name {
          display: flex;
          align-items: center;
          gap: 1rem;
          min-height: 3.5rem;
          padding: .5rem 1.25rem;
          border-bottom: 1px solid rgba(255, 255, 255, 0.05);
          cursor: text;
        }
        .avatar-name span {
          flex: 1;
          color: rgba(255, 255, 255, 0.6);
          font-size: .9375rem;
        }
        .avatar-name input {
          min-width: 0;
          width: 14rem;
          padding: .5rem .65rem;
          color: white;
          background: rgba(255, 255, 255, 0.06);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: .5rem;
          font: inherit;
          outline: none;
        }
        .avatar-name input:focus { border-color: rgba(255, 255, 255, 0.65); }
        p { margin: 0; color: rgba(255, 255, 255, 0.6); font-size: .9375rem; line-height: 1.45; }
        .avatar-chooser-content { padding: 1.25rem; }
        .avatar-intro { margin-bottom: 1.25rem; }
        .avatar-options { display: grid; grid-template-columns: repeat(2, 1fr); gap: .625rem; }
        .avatar-option {
          min-height: 9rem;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: .8rem;
          background: rgba(255, 255, 255, 0.035);
          color: rgba(255, 255, 255, 0.8);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: .75rem;
          cursor: pointer;
          font: inherit;
          transition: background .15s, border-color .15s;
        }
        .avatar-option:hover { background: rgba(255, 255, 255, 0.08); color: white; }
        .avatar-option[aria-pressed='true'] {
          border-color: rgba(255, 255, 255, 0.75);
          background: rgba(255, 255, 255, 0.1);
          color: white;
        }
        .avatar-figure { width: 2.25rem; height: 2.25rem; stroke-width: 1.5; }
        .avatar-preview {
          display: block;
          width: 7.15rem;
          height: 8.45rem;
          object-fit: contain;
          object-position: center;
          border-radius: .5rem;
        }
        .avatar-actions {
          display: flex;
          align-items: stretch;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
        }
        .avatar-actions button {
          flex: 1;
          min-height: 2.7rem;
          border: 0;
          background: transparent;
          color: rgba(255, 255, 255, 0.8);
          cursor: pointer;
          font: inherit;
        }
        .avatar-actions button + button { border-left: 1px solid rgba(255, 255, 255, 0.1); }
        .avatar-actions button:hover { color: white; background: rgba(255, 255, 255, 0.05); }
        .avatar-note { margin-top: .9rem; }
        .avatar-actions button:disabled, .avatar-option:disabled { opacity: .55; cursor: wait; }
        .avatar-error { margin-top: .8rem; color: #ffb1a5; font-size: .9375rem; }
        @media (max-width: 480px) {
          .avatar-name { align-items: stretch; flex-direction: column; gap: .4rem; padding-block: .75rem; }
          .avatar-name input { width: 100%; }
          .avatar-options { grid-template-columns: 1fr; }
          .avatar-option { min-height: 4rem; flex-direction: row; }
          .avatar-figure { font-size: 2rem; }
          .avatar-preview { width: 3.9rem; height: 3.9rem; }
        }
      `}
    >
      <section className='avatar-chooser' aria-label='Scelta avatar'>
        <label className='avatar-name'>
          <span>Il tuo nome</span>
          <input
            type='text'
            value={name}
            placeholder='Inserisci il tuo nome'
            required={required}
            aria-required={required}
            onFocus={event => event.target.select()}
            onChange={event => setName(event.target.value)}
            onKeyDown={event => {
              if (event.key === 'Enter') {
                event.preventDefault()
                saveName()
                event.currentTarget.blur()
              }
            }}
            onBlur={saveName}
          />
        </label>
        <h2>Scegli il tuo avatar</h2>
        <div className='avatar-chooser-content'>
          <p className='avatar-intro'>
            {required
              ? 'Inserisci il tuo nome e seleziona Campus A o Campus B per continuare.'
              : 'Puoi cambiare scelta in seguito. Il tuo avatar personale resta associato a questo profilo.'}
          </p>
          <div className='avatar-options'>
            {catalog.map(item => (
              <button
                className='avatar-option'
                key={item.id}
                type='button'
                aria-pressed={selected === item.url}
                disabled={busy}
                onClick={() => selectAvatar(item.url)}
              >
                {item.image ? (
                  <img
                    className='avatar-preview'
                    src={world.resolveURL(item.image)}
                    alt=''
                    aria-hidden='true'
                  />
                ) : (
                  <UserRoundIcon className='avatar-figure' aria-hidden='true' />
                )}
                <span>{item.name}</span>
              </button>
            ))}
          </div>
          {!required && <p className='avatar-note'>File VRM fino a 10 MB. Un caricamento al minuto.</p>}
          {error && <div className='avatar-error' role='alert'>{error}</div>}
        </div>
        {!required && <div className='avatar-actions'>
          <button type='button' disabled={busy} onClick={() => fileRef.current?.click()}>Carica il tuo .vrm</button>
          <button type='button' disabled={busy} onClick={onClose}>Continua</button>
          <input
            ref={fileRef}
            type='file'
            accept='.vrm'
            style={{ display: 'none' }}
            tabIndex={-1}
            onChange={event => upload(event.target.files?.[0])}
          />
        </div>}
      </section>
    </div>
  )
}
